<template>
  <!-- Encaje económico por partida: solo lectura, calculado de la Plantilla PRM del proyecto. -->
  <summary-card :title="$t('prm_dash_economics_title')" :subtitle="$t('prm_dash_economics_subtitle')" :mode="mode" chip="prm">
    <template #icon><b-icon pack="fas" icon="coins" size="is-small"></b-icon></template>

    <div v-if="!hasPrm" class="empty-state">{{ $t('prm_dash_economics_no_prm') }}</div>

    <template v-else>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key" :class="thClass(col)">
                <span class="th-in">
                  <span>{{ $t(col.label) }}</span>
                  <source-chip v-if="mode && col.source" :source="col.source"></source-chip>
                </span>
              </th>
              <th class="c"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td v-for="col in columns" :key="col.key" :class="tdClass(col, row)" :style="cellStyle(col, row)">{{ cell(col, row) }}</td>
              <td class="c"><sem-dot :state="row.color"></sem-dot></td>
            </tr>
            <tr class="total">
              <td v-for="col in columns" :key="col.key" :class="tdClass(col, total)">{{ cell(col, total) }}</td>
              <td class="c"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="foot-note">
        <b-icon pack="fas" icon="file-lines" size="is-small"></b-icon>
        <span>{{ $t('prm_dash_economics_footer') }}</span>
      </div>
    </template>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import SemDot from './sem-dot.vue'
  import { fmtEur, fmtPct, fmtEurSigned } from '../shared/format'
  import { coWeights, computeAll, computeTotals, flatten } from '../template/template-model'

  export default {
    name: 'PrmDashEconomics',

    components: { SummaryCard, SourceChip, SemDot },

    props: {
      template: { type: Object, default: null },
      mode: { type: Boolean, default: false },
    },

    computed: {
      hasPrm () { return this.template != null && Array.isArray(this.template.structure) && this.template.structure.length > 0 },

      // Columnas: fuente por columna (PRM = presupuestos/desviaciones/COs/incurrido; Calc = derivadas;
      // Contract = penalidades, que vienen de los hitos penalizables del Resumen).
      columns () {
        return [
          { key: 'name', label: 'prm_dash_eco_item', align: 'l', kind: 'name' },
          { key: 'ec', label: 'prm_dash_eco_commercial', source: 'prm', align: 'r', kind: 'eur' },
          { key: 'ep', label: 'prm_dash_eco_projects', source: 'prm', align: 'r', kind: 'eur' },
          { key: 'deviations', label: 'prm_dash_eco_deviations', source: 'prm', align: 'r', kind: 'eurSigned', bad: true },
          { key: 'coApproved', label: 'prm_dash_eco_co_approved', source: 'prm', align: 'r', kind: 'eur' },
          { key: 'coNegotiation', label: 'prm_dash_eco_co_negotiation', source: 'prm', align: 'r', kind: 'eur', amber: true },
          { key: 'penalties', label: 'prm_dash_eco_penalties', source: 'contract', align: 'r', kind: 'eur' },
          { key: 'totalEstimate', label: 'prm_dash_eco_total_cost', source: 'calc', align: 'r', kind: 'eur', strong: true },
          { key: 'backlog', label: 'prm_dash_eco_backlog', source: 'calc', align: 'r', kind: 'eur' },
          { key: 'incurredMonth', label: 'prm_dash_eco_incurred_month', source: 'calc', align: 'r', kind: 'eur' },
          { key: 'incurredTotal', label: 'prm_dash_eco_incurred_total', source: 'prm', align: 'r', kind: 'eur' },
          { key: 'workPct', label: 'prm_dash_eco_poc', source: 'calc', align: 'c', kind: 'pct' },
        ]
      },

      // Ambos escenarios: confirmadas (aprobadas) y previstas (en negociación).
      calc () {
        const t = this.template
        const structure = t.structure || []
        const cos = (t.columns && t.columns.cos) || []
        const monthIds = ((t.columns && t.columns.months) || []).map((c) => c.id)
        const coIds = cos.map((c) => c.id)
        const values = t.values || {}
        const confirmed = computeAll(structure, values, monthIds, coIds, coWeights(cos, 'confirmed'))
        const expected = computeAll(structure, values, monthIds, coIds, coWeights(cos, 'expected'))
        return { structure, monthIds, coIds, confirmed, expected }
      },

      rows () {
        if (!this.hasPrm) return []
        const { structure, confirmed, expected } = this.calc
        // Solo grupos de coste: mostramos el resumen por grupo, no el detalle de hojas ni la venta.
        // computeAll ya agrega los hijos en el grupo, así que el valor del grupo viene sumado.
        return flatten(structure)
          .filter(({ node }) => node.role === 'group')
          .map(({ node }) => this.rowOf(node, confirmed[node.id], expected[node.id]))
      },

      total () {
        if (!this.hasPrm) return {}
        const { structure, monthIds, coIds, confirmed, expected } = this.calc
        const c = computeTotals(confirmed, structure, monthIds, coIds).cost
        const e = computeTotals(expected, structure, monthIds, coIds).cost
        // Las penalidades no se reparten por partida ni se suman aquí (la penalidad total
        // contractual vive en Revisión contractual); la columna queda a "-".
        return this.rowOf({ label: this.$t('prm_dash_eco_total') }, c, e)
      },
    },

    methods: {
      // Fila común (partida o total) desde su cálculo confirmado + previsto.
      rowOf (node, c, e) {
        c = c || {}
        e = e || {}
        const ec = c.ec
        const totalCo = c.totalCo
        const totalEstimate = c.totalEstimate
        // Semáforo por sobrecoste sobre el encaje comercial (mismo criterio que el diseño).
        const ratio = ec ? ((this.n(totalEstimate) - this.n(totalCo)) / ec) : 1
        const color = ratio <= 1.002 ? 'green' : ratio <= 1.02 ? 'amber' : 'red'
        return {
          id: node.id || 'total',
          name: node.label,
          ec, ep: c.ep, deviations: c.deviations, totalEstimate,
          coApproved: totalCo,
          coNegotiation: e.totalCo,
          penalties: null, // por partida no se reparte (ver total)
          backlog: c.pendingCost,
          incurredMonth: c.incurredMonth,
          incurredTotal: c.incurredTotal,
          workPct: c.workPct,
          color,
        }
      },

      // Texto de una celda según el tipo de columna.
      cell (col, row) {
        const v = row[col.key]
        if (col.kind === 'name') return row.name
        if (col.kind === 'pct') return v == null ? '-' : fmtPct(v)
        if (col.kind === 'eurSigned') return v == null ? '-' : fmtEurSigned(v)
        return v == null ? '-' : fmtEur(v)
      },

      thClass (col) { return col.align === 'r' ? 'r' : col.align === 'c' ? 'c' : '' },
      tdClass (col, row) {
        return {
          r: col.align === 'r',
          c: col.align === 'c',
          strong: col.strong,
          bad: col.bad && this.n(row[col.key]) > 0,
        }
      },
      // Color ámbar para las COs en negociación con valor.
      cellStyle (col, row) {
        return col.amber && this.n(row[col.key]) > 0 ? { color: '#b9770e' } : {}
      },
      n (v) { return Number(v) || 0 },
    },
  }
</script>

<style lang="scss" scoped>
  .empty-state { padding: 20px; text-align: center; color: #9aa4b2; font-size: 11px; }
  .tbl-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; padding: 5px 8px; text-align: left; white-space: nowrap; vertical-align: top; }
  th.r { text-align: right; }
  th.c { text-align: center; }
  // Etiqueta + chip de fuente apilados en la cabecera.
  .th-in { display: inline-flex; flex-direction: column; gap: 2px; }
  th.r .th-in { align-items: flex-end; }
  th.c .th-in { align-items: center; }
  .th-in ::v-deep .src-chip { margin-left: 0; }
  td { border-bottom: 1px solid #eef0f3; padding: 4px 8px; font-size: 11px; color: #2c3a4d; vertical-align: middle; white-space: nowrap; }
  td.r { text-align: right; font-variant-numeric: tabular-nums; }
  td.c { text-align: center; }
  td.strong { font-weight: 600; }
  td.bad { color: #c0392b; }
  // Cebra + hover (para revisar mejor); la fila TOTAL conserva su fondo.
  tbody tr:nth-child(even):not(.total) td { background: #f9fafb; }
  tbody tr:hover:not(.total) td { background: #eef2f7; }
  tr.total td { background: #eef3f9; font-weight: 700; }

  // Nota informativa sutil (como el diseño): icono verde pequeño + texto gris, fondo muy tenue.
  .foot-note {
    display: flex; align-items: flex-start; gap: 6px; padding: 7px 10px;
    font-size: 10px; color: #7a8798; line-height: 1.4; background: #f7faf8; border-top: 1px solid #eef0f3;
  }
  .foot-note ::v-deep .icon { color: #2e7d5b; flex-shrink: 0; }
</style>
